import { Container, Box, Stack, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";

import { useAppSelector } from "~/app/store";
import { useFlag } from "~/config/flags";

import {
  selectPreviousBinNumber,
  selectPreviousPutawayTask
} from "~/features/autostorePutaway/autostorePutaway.slice";
import {
  getBinCompartmentOccupancy,
  isBinResponseCompartmentEmpty
} from "~/lib/binCompartmentOccupancy";
import { mixpanelTrack } from "~/lib/mixpanel-tracking";
import { NextBinInventory } from "~/redux/reducers/autostore";

import { selectBinsFlags } from "~/redux/selectors/autostoreSelectors";
import { WorkstationSummaryDto, NextEmptyBinResponse } from "~/types/api";

import { AutostoreBinWithAnimation } from "./AutostoreBinWithAnimation";

function MultiPort(props: {
  workstation: WorkstationSummaryDto | null;
  /** Empty bin info for each port in workstation */
  nextEmptyBinByPort?: {
    [portId: number]: NextEmptyBinResponse | null;
  };
  /** Non-Empty bin info for each port in workstation */
  nextBinInventoryByPort?: {
    [portId: number]: NextBinInventory;
  };
  currentSelectedBinId?: number | null;
  /** Qty to display in the selected compartment */
  pickQuantity?: number;
  selectedCompartment?: number;
  onBinClick?: (compartment?: number, binNumber?: number) => void;
  allowSelectingCompartmentsWithInventoryOnly?: boolean;
  allowSelectingCompartmentsWithoutInventoryOnly?: boolean;
  selectedBin?: NextEmptyBinResponse | null;
  setSelectedBinCallback?: (bin: NextEmptyBinResponse) => void;
  selectedSummariesAtPort?: string[];
  showBinsFlags?: boolean;
  renderBinButton?: (bin: NextEmptyBinResponse) => React.ReactNode;
  renderBelowBinElement?: (binId: number) => React.ReactNode;
  requiresWholeBin?: boolean;
  // this is a temporary prop until all pages are updated with shortside bin logic, TODO: switch to useAppSelector(selectIsShortSideBin)
  isShortSideBin?: boolean;
  /** Optional page name for Mixpanel tracking when bin is clicked */
  trackedPageName?: string;
  /** Controls bin ID visibility while waiting for a bin. */
  showBinIdWhenWaiting?: boolean;
}) {
  const {
    pickQuantity = 0,
    selectedCompartment,
    workstation,
    nextEmptyBinByPort,
    nextBinInventoryByPort,
    currentSelectedBinId,
    onBinClick,
    allowSelectingCompartmentsWithInventoryOnly,
    allowSelectingCompartmentsWithoutInventoryOnly,
    selectedBin,
    setSelectedBinCallback,
    selectedSummariesAtPort,
    showBinsFlags,
    renderBinButton,
    renderBelowBinElement,
    requiresWholeBin,
    isShortSideBin,
    trackedPageName,
    showBinIdWhenWaiting
  } = props;

  const { t } = useTranslation();
  const { palette } = useTheme();
  const { enableColorChanges } = useFlag();

  const binsFlags = useAppSelector(selectBinsFlags);
  const previousBinNumber = useAppSelector(selectPreviousBinNumber);
  const previousPutawayTask = useAppSelector(selectPreviousPutawayTask);
  const portOverrides = useAppSelector(
    (state) => state.autostorePutaway.wholeBinPortOverrides
  );
  const hasSelectedAlreadyWholeBins = useAppSelector(
    (state) => state.autostorePutaway.hasSelectedAlreadyWholeBins
  );
  const hasDeclinedPortOverride = useAppSelector(
    (state) => state.autostorePutaway.hasDeclinedPortOverride
  );

  // Active after confirming overrides, confirming whole bins only, or dismissing the modal
  const isFoodSafetyMode =
    !!portOverrides.length ||
    hasSelectedAlreadyWholeBins ||
    hasDeclinedPortOverride;

  const binConfigMap: {
    [key: string]: {
      horizontalCompartmentCount: number;
      numberOfCompartments: number;
      verticalCompartmentCount: number;
    };
  } = {
    "whole bin": {
      horizontalCompartmentCount: 1,
      numberOfCompartments: 1,
      verticalCompartmentCount: 1
    },
    "half bin": {
      horizontalCompartmentCount: 1,
      numberOfCompartments: 2,
      verticalCompartmentCount: 2
    },
    "quarter bin": {
      horizontalCompartmentCount: 2,
      numberOfCompartments: 4,
      verticalCompartmentCount: 2
    }
  };

  return (
    <Container
      aria-label="MultiPort"
      style={{ height: "100%" }}
      maxWidth={isShortSideBin ? "md" : "lg"}
    >
      <Box
        component="ul"
        sx={{
          listStyleType: "none",
          pl: 0,
          display: "grid",
          gridTemplateColumns: `repeat(${workstation?.ports.length}, minmax(200px, 1fr))`,
          gap: {
            sm: 4,
            lg: 5
          },
          m: 0,
          height: "inherit"
        }}
      >
        {workstation?.ports.map((port, i) => {
          const { portId } = port;
          /** Whole bin, half bin, quarter bin */
          const portConfig =
            (port.inductionBinConfiguration.length &&
              port.inductionBinConfiguration[0].toLowerCase()) ||
            "whole bin";
          const binConfigCompartments = binConfigMap[portConfig];
          const emptyBinAtThisPort = nextEmptyBinByPort
            ? nextEmptyBinByPort[portId]
            : null;
          const nextInventoryBinAtThisPort = nextBinInventoryByPort
            ? nextBinInventoryByPort[portId]
            : null;
          const numberOfColumns =
            emptyBinAtThisPort?.autostoreBinConfiguration
              ?.verticalCompartmentCount ||
            nextInventoryBinAtThisPort?.nextBinConfiguration
              ?.verticalCompartmentCount ||
            binConfigCompartments.verticalCompartmentCount;
          const numberOfRows =
            emptyBinAtThisPort?.autostoreBinConfiguration
              ?.horizontalCompartmentCount ||
            nextInventoryBinAtThisPort?.nextBinConfiguration
              ?.horizontalCompartmentCount ||
            binConfigCompartments.horizontalCompartmentCount;
          const binId =
            emptyBinAtThisPort?.openBinResponse?.binId ||
            nextInventoryBinAtThisPort?.nextBinInventory[0]?.bin.autostoreBin
              ?.autostoreBinId;
          const isThisPortSelected =
            currentSelectedBinId && currentSelectedBinId === binId;
          const isThisBinSelected =
            selectedBin &&
            (selectedBin === emptyBinAtThisPort ||
              selectedBin === nextInventoryBinAtThisPort?.nextBinResponse);
          const compartmentOccupancy = getBinCompartmentOccupancy(
            nextInventoryBinAtThisPort
              ? nextInventoryBinAtThisPort?.nextBinResponse
              : emptyBinAtThisPort
          );

          // get defaultSelected compartment number?
          const defaultCompartmentInventoryRecord = nextInventoryBinAtThisPort
            ? nextInventoryBinAtThisPort?.nextBinResponse?.inventory.find(
                (inventoryRecord) => {
                  if (
                    inventoryRecord?.inventoryId &&
                    inventoryRecord.bin?.autostoreCompartmentNumber
                  ) {
                    return selectedSummariesAtPort?.includes(
                      inventoryRecord?.inventoryId
                    ) && inventoryRecord.bin?.autostoreCompartmentNumber
                      ? inventoryRecord?.bin.autostoreCompartmentNumber
                      : undefined;
                  }
                  return undefined;
                }
              )
            : undefined;

          const defaultCompartmentNumber =
            defaultCompartmentInventoryRecord &&
            defaultCompartmentInventoryRecord?.bin?.autostoreCompartmentNumber
              ? defaultCompartmentInventoryRecord.bin
                  .autostoreCompartmentNumber - 1
              : undefined;

          const binFlags = binsFlags?.find((b) => b.binId === binId)?.flags;
          const binBorder =
            (!selectedBin &&
              isThisPortSelected &&
              workstation.ports.length > 1) ||
            isThisBinSelected
              ? enableColorChanges
                ? palette.autostoreDesignSystem.border.onStandard
                    .statusInformative
                : "primary.main"
              : "transparent";

          // show bin as inactive if in previous bin flow, but its not the previous bin
          // or if in food safety or sanitize item selected and the bin is not whole
          const isWholeBin = numberOfRows === 1 && numberOfColumns === 1;
          const isBinInactive =
            (previousBinNumber && previousBinNumber !== binId) ||
            (isFoodSafetyMode && requiresWholeBin && !isWholeBin);

          const isPreviousBin =
            !!previousBinNumber && previousBinNumber === binId;

          const getSelectedCompartment = () => {
            if (isThisPortSelected) {
              return selectedCompartment;
            }
            if (isPreviousBin) {
              return previousPutawayTask?.compartment;
            }
            return defaultCompartmentNumber;
          };

          return (
            <Stack
              key={portId}
              component="li"
              aria-label={`port ${portId}`}
              aria-selected={!!isThisPortSelected || !!isThisBinSelected}
              direction="column"
              alignItems="center"
              justifyContent="center"
            >
              {showBinsFlags && (
                <Box
                  display="flex"
                  alignContent="center"
                  justifyContent="center"
                  sx={{ height: 32 }}
                >
                  {binFlags?.length ? (
                    <Typography variant="h6">
                      {t("flag")}: {binFlags.join(", ")}
                    </Typography>
                  ) : null}
                </Box>
              )}
              <Stack
                alignItems="center"
                sx={{
                  padding: 1,
                  borderRadius: "0.5em",
                  border: `0.625em solid`,
                  borderColor: binBorder,
                  height: isShortSideBin ? "100%" : "auto",
                  width: isShortSideBin ? "fit-content" : "100%",
                  justifySelf: isShortSideBin ? "center" : "unset"
                }}
                role="region"
                aria-label={`Bin container ${i}`}
              >
                <AutostoreBinWithAnimation
                  key={`${port.portId}${binId || "no-bin"}`}
                  portId={portId}
                  stateOverride={isBinInactive ? "Inactive" : undefined}
                  numberOfRows={numberOfRows}
                  numberOfColumns={numberOfColumns}
                  pickQuantity={pickQuantity}
                  pickCompartment={null}
                  hideBinId={false}
                  showBinIdWhenWaiting={showBinIdWhenWaiting}
                  binId={binId}
                  compartmentOccupancy={compartmentOccupancy}
                  selectedCompartment={getSelectedCompartment()}
                  onBinClick={(compartment) => {
                    if (compartment === undefined || isBinInactive) {
                      return;
                    }

                    if (allowSelectingCompartmentsWithoutInventoryOnly) {
                      // Note: 'compartment' is numbered 0-3 while 'autostoreCompartmentNumber' is 1-4
                      const isCompartmentEmpty = isBinResponseCompartmentEmpty(
                        emptyBinAtThisPort,
                        compartment
                      );

                      if (!isCompartmentEmpty) {
                        return;
                      }
                    }

                    if (setSelectedBinCallback) {
                      if (emptyBinAtThisPort)
                        setSelectedBinCallback(emptyBinAtThisPort);
                      else if (nextInventoryBinAtThisPort)
                        setSelectedBinCallback(
                          nextInventoryBinAtThisPort.nextBinResponse
                        );
                    }

                    if (onBinClick && binId) {
                      onBinClick(compartment, binId);
                    }

                    if (trackedPageName) {
                      mixpanelTrack({
                        trackedPageName,
                        type: "Button Click",
                        label: "Select Bin Compartment",
                        eventProperties: {
                          BinId: binId,
                          Compartment: compartment,
                          PortId: portId
                        }
                      });
                    }
                  }}
                  allowSelectingCompartmentsWithInventoryOnly={
                    allowSelectingCompartmentsWithInventoryOnly
                  }
                  renderBinButton={
                    renderBinButton && emptyBinAtThisPort
                      ? renderBinButton(emptyBinAtThisPort)
                      : undefined
                  }
                  isShortSideBin={isShortSideBin}
                />
              </Stack>
              {renderBelowBinElement &&
                emptyBinAtThisPort?.openBinResponse?.binId &&
                renderBelowBinElement(
                  emptyBinAtThisPort?.openBinResponse?.binId
                )}
            </Stack>
          );
        })}
      </Box>
    </Container>
  );
}

export default MultiPort;
